import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

const emptyForm = {
  text: '',
  is_active: true,
  sort_order: 0,
};

function AdminSiteAnnouncementsPage() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const dragIndexRef = React.useRef(-1);

  const [announcements, setAnnouncements] = React.useState([]);

  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);

  const fetchAll = async () => {
    setError('');
    try {
      const res = await pb.collection('site_announcements').getList(1, 200, { sort: 'sort_order, -created' });
      setAnnouncements(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des promotions');
      setAnnouncements([]);
    }
  };

  React.useEffect(() => {
    fetchAll();
  }, []);

  const persistOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_announcements').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setAnnouncements((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndexRef.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndexRef.current = -1;
    const ordered = [...announcements];
    await persistOrder(ordered);
    await fetchAll();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, sort_order: announcements.length ? Math.max(...announcements.map(a => Number(a.sort_order || 0))) + 1 : 0 });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      text: a?.text || '',
      is_active: a?.is_active !== false,
      sort_order: Number(a?.sort_order || 0),
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSaveAnnouncement = async () => {
    setLoading(true);
    try {
      const payload = {
        text: form.text || '',
        is_active: !!form.is_active,
        sort_order: Number(form.sort_order || 0),
      };

      if (editing?.id) {
        await pb.collection('site_announcements').update(editing.id, payload);
      } else {
        await pb.collection('site_announcements').create(payload);
      }
      await fetchAll();
      closeModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await pb.collection('site_announcements').delete(id);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (a) => {
    try {
      await pb.collection('site_announcements').update(a.id, { is_active: !(a?.is_active !== false) });
      await fetchAll();
    } catch (e) {
      alert(e?.message || "Erreur lors de la mise à jour");
    }
  };

  return (
    <AdminLayout title="Promotions">
      <h1 style={{ marginTop: 0 }}>Promotions</h1>
      <p>Gérez la barre de promotion sous la navbar.</p>

      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .toggle-pill{ cursor:pointer; border:1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); padding:6px 10px; border-radius:999px; font-size:.85rem; }
        .toggle-pill.on{ border-color: rgba(34,197,94,0.55); box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
        .toggle-pill.off{ opacity:.8; }
        .grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 12px; }
        @media (max-width: 480px){ .grid{ grid-template-columns: 1fr; } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 920px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }

        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="number"], .field textarea{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field textarea{ min-height: 120px; resize: vertical; }
        .field input:focus, .field textarea:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }

        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }
        .btn-danger{ border-color: rgba(239,68,68,0.5); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0 }}>Barre de promotion</h3>
          <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Messages affichés sous la navbar (rotation via la croix).</div>
        </div>
        <button className="btn btn-solid btn-primary" onClick={openCreate}>Nouvelle promotion</button>
      </div>

      <div className="grid">
        {announcements.map((a, idx) => (
          <div
            key={a.id}
            className="card"
            draggable
            onDragStart={(e) => onDragStart(e, idx)}
            onDragOver={(e) => onDragOver(e, idx)}
            onDrop={(e) => {
              e.preventDefault();
              onDrop();
            }}
            title={`Glissez pour réordonner (position ${idx + 1})`}
            style={{ cursor: 'grab' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ fontWeight: 800, lineHeight: 1.2 }}>{a.text || '(Sans texte)'}</div>
              <button className={`toggle-pill ${(a?.is_active !== false) ? 'on' : 'off'}`} onClick={() => handleToggleActive(a)}>
                {(a?.is_active !== false) ? 'Actif' : 'Inactif'}
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem' }}>Ordre: {Number(a?.sort_order || 0)}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={() => openEdit(a)}>Modifier</button>
                <button className="btn btn-danger" onClick={() => handleDelete(a.id)}>Supprimer</button>
              </div>
            </div>
          </div>
        ))}
        {announcements.length === 0 && (
          <div style={{ opacity: 0.8 }}>Aucune promotion. Clique “Nouvelle promotion”.</div>
        )}
      </div>

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
          if (e.target?.classList?.contains('modal-overlay')) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editing?.id ? 'Modifier la promotion' : 'Nouvelle promotion'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Texte</label>
                <textarea value={form.text} onChange={(e) => setForm(f => ({ ...f, text: e.target.value }))} placeholder="❄️ Offres d'hiver exclusives ! Jusqu'à -5% sur toutes nos offres." />
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} />
              </div>
              <div className="field">
                <label>Actif</label>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-solid" onClick={handleSaveAnnouncement} disabled={loading}>
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSiteAnnouncementsPage;
